import { create } from 'zustand';

import { apiClient, clearTokens, hasSession, setTokens, tokenEvents } from '@/shared/api';
import type { AuthTokens, SessionEndReason } from '@/shared/api';

/**
 * Tells the backend to revoke the refresh-token family and clear the httpOnly
 * cookie.
 *
 * Called through `apiClient` rather than `features/auth`'s `authApi`, because
 * `shared` must not import from `features` — the dependency only ever points
 * the other way.
 */
async function signOutOnServer(): Promise<void> {
  try {
    await apiClient.post<null>('/api/v1/auth/logout', undefined, { skipRefresh: true });
  } catch {
    /* the local session is already gone; a failed revocation is not actionable here */
  }
}

/**
 * Session state only.
 *
 * Zustand's remit in Novix is deliberately small: session and UI. Everything
 * that comes from the server belongs to TanStack Query. Mixing the two is how
 * you end up with two sources of truth for a balance.
 *
 * Note there is no `accessToken` field here — the token lives in the API layer's
 * module scope and is never exposed to React state, where it would end up in
 * devtools, in a React DevTools export, or in an error report.
 */

export interface SessionUser {
  /**
   * The account id from `/account/me`, as a string.
   *
   * `null` means "not read yet", never "zero". It is a `long` on the wire and
   * the only handle another customer needs to send money here, so it is carried
   * as a string end to end rather than round-tripped through a JS number.
   */
  id: string | null;
  email: string;
}

/**
 * `unknown` is the state the app STARTS in, and it is the reason the gate can
 * promise never to flash protected content.
 *
 * The access token lives in memory only, so after a reload we hold nothing —
 * yet the refresh cookie may still be perfectly valid. Booting straight into
 * `anonymous` would bounce a signed-in user to /welcome on every refresh;
 * booting into `authenticated` would paint a wallet that is about to 401.
 * So the app starts in `unknown`, the gate runs one silent restore, and only
 * its answer moves the state on.
 *
 * `unavailable` is the fourth answer: the check itself could not complete
 * (offline, timeout, refresh endpoint rate-limited). That is NOT "signed out" —
 * we do not know — and it must not be shown as an infinite spinner either.
 */
export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous' | 'unavailable';

interface SessionState {
  status: SessionStatus;
  user: SessionUser | null;
  /**
   * Set when the session ended by itself, so the login screen can explain why.
   * `session-revoked` in particular must read differently from a plain expiry —
   * see SessionEndReason.
   */
  expiredReason: SessionEndReason | null;
  signIn: (tokens: AuthTokens, user?: SessionUser | null) => void;
  setUser: (user: SessionUser | null) => void;
  signOut: () => void;
  markExpired: (reason: SessionEndReason) => void;
  clearExpiredReason: () => void;
  /** A silent restore found a live session. */
  sessionRestored: (user: SessionUser) => void;
  /**
   * A silent restore found no session. Distinct from `markExpired`: this user
   * never had one in this browser, so `expiredReason` is cleared — they get
   * /welcome, not a login screen apologising for an expiry that never happened.
   */
  sessionAbsent: () => void;
  /** The restore could not complete. Neither in nor out; see SessionStatus. */
  sessionCheckFailed: () => void;
  /** Back to `unknown` so the gate retries the restore. */
  retrySessionCheck: () => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  status: hasSession() ? 'authenticated' : 'unknown',
  user: null,
  expiredReason: null,
  signIn: (tokens, user) => {
    setTokens(tokens);
    set({ status: 'authenticated', user: user ?? null, expiredReason: null });
  },
  setUser: (user) => {
    set({ user });
  },
  sessionRestored: (user) => {
    set({ status: 'authenticated', user, expiredReason: null });
  },
  sessionAbsent: () => {
    set({ status: 'anonymous', user: null, expiredReason: null });
  },
  sessionCheckFailed: () => {
    set({ status: 'unavailable', user: null });
  },
  retrySessionCheck: () => {
    set({ status: 'unknown' });
  },
  signOut: () => {
    // Local state first, and unconditionally. The server call clears the
    // httpOnly cookie and revokes the token family, but the user pressed "sign
    // out" and must be signed out whether or not the network cooperates —
    // leaving them on an authenticated screen while a request retries is the
    // one outcome that is clearly wrong. A failure here is swallowed on
    // purpose: there is nothing the user could do about it, and the access
    // token in memory is already gone.
    clearTokens('logout');
    set({ status: 'anonymous', user: null, expiredReason: null });
    void signOutOnServer();
  },
  markExpired: (reason) => {
    /*
     * While the app is still in `unknown` the silent restore owns the outcome,
     * so a transport-level expiry here is not news — it IS the check, and the
     * restore is one line away from recording a more precise answer.
     *
     * Acting on it anyway is a race with teeth: the failed refresh clears the
     * token store and emits `expired` BEFORE the restore's catch block runs, so
     * the store would flip to `anonymous`, React would paint, and the gate would
     * navigate to /login — a fraction of a second before the restore concluded
     * that the server was merely unreachable and the user should have been shown
     * the retry screen instead.
     */
    if (useSessionStore.getState().status === 'unknown') return;
    set({ status: 'anonymous', user: null, expiredReason: reason });
  },
  clearExpiredReason: () => {
    set({ expiredReason: null });
  }
}));

/** Wires transport-level session loss into the store. Called once at startup. */
export function bindSessionToTokenEvents(): () => void {
  return tokenEvents.on('expired', ({ reason }) => {
    if (reason === 'logout') return;
    useSessionStore.getState().markExpired(reason);
  });
}
